import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Input, Textarea } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FormRow } from '../../components/ui/FormRow';
import { Spinner } from '../../components/ui/Spinner';
import {
  ImagenPrincipalCard,
  GaleriaAdicionalCard,
  FichaCard,
  UserGuideCard,
  SpecsCard,
} from '../../components/ProductoProveedorArchivos';
import { fmt } from '../../lib/formatters';
import { useVolver } from '../../lib/useVolver';
import { toast, confirmar } from '../../lib/toast';
import { coloresDePrecios, normalizarColor, precioParaColor } from '../../../shared/precio-color';

/** Sugerencias del campo Color (se puede teclear cualquier otro). */
const COLORES_HABITUALES = ['NEGRO', 'BLANCO', 'GRIS', 'PLATA', 'AZUL', 'ROJO', 'ROSA', 'VERDE'];

type PrecioRow = {
  id: string;
  fecha: string;
  tramo1_unidades: number | null;
  tramo1_precio_usd: number | null;
  tramo2_unidades: number | null;
  tramo2_precio_usd: number | null;
  tramo3_unidades: number | null;
  tramo3_precio_usd: number | null;
  opciones: Array<{ descripcion: string; importe_usd: number; incluido: boolean }> | null;
  notas: string | null;
  /** Color al que aplica el precio; null = precio general (shared/precio-color.ts). */
  color: string | null;
};

type ProductoProveedorFull = {
  id: string;
  proveedor_id: string;
  proveedor_nombre: string | null;
  proveedor_foc_pct: number | null;
  ref_proveedor: string;
  nombre: string;
  descripcion: string | null;
  main_specs: string | null;
  gama_id: string | null;
  gama_nombre: string | null;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_id: string | null;
  subcategoria_nombre: string | null;
  detalle_id: string | null;
  detalle_nombre: string | null;
  moq: number | null;
  lead_time_dias: number | null;
  envio_tipo: 'FOB' | 'EXW';
  packaging_included: boolean;
  foc_pct: number | null;
  units_40hq: number | null;
  unit_box_largo_cm: number | null;
  unit_box_ancho_cm: number | null;
  unit_box_alto_cm: number | null;
  unit_box_peso_kg: number | string | null;
  ctn_largo_cm: number | null;
  ctn_ancho_cm: number | null;
  ctn_alto_cm: number | null;
  ctn_peso_kg: number | string | null;
  cbm_ctn: number | string | null;
  carton_box_pcs: number | null;
  // Transporte heredable (cascada producto > detalle > subcat > categoría).
  detalle_units_40hq: number | null;
  detalle_transporte_base: number | string | null;
  subcat_units_40hq: number | null;
  subcat_transporte_base: number | string | null;
  categoria_transporte_base: number | string | null;
  notas: string | null;
  activo: boolean;
  sample: 'SOLICITADO' | 'SI' | 'NO' | null;
  precio_target_usd: number | string | null;
  origen: string | null;
  created_at: string;
  updated_at: string;
  precios: PrecioRow[];
};

// ─── Draft ────────────────────────────────────────────────────────────

type Draft = {
  ref_proveedor: string;
  nombre: string;
  main_specs: string;
  descripcion: string;
  notas: string;
  gama_id: string;
  categoria_id: string;
  subcategoria_id: string;
  detalle_id: string;
  moq: string;
  lead_time_dias: string;
  envio_tipo: 'FOB' | 'EXW';
  packaging_included: boolean;
  foc_pct: string;
  units_40hq: string;
  unit_box_largo_cm: string;
  unit_box_ancho_cm: string;
  unit_box_alto_cm: string;
  unit_box_peso_kg: string;
  ctn_largo_cm: string;
  ctn_ancho_cm: string;
  ctn_alto_cm: string;
  ctn_peso_kg: string;
  cbm_ctn: string;
  carton_box_pcs: string;
  origen: string;
  sample: '' | 'SOLICITADO' | 'SI' | 'NO';
  precio_target_usd: string;
  activo: boolean;
};

function draftFromProducto(p: ProductoProveedorFull): Draft {
  return {
    ref_proveedor: p.ref_proveedor,
    nombre: p.nombre,
    main_specs: p.main_specs ?? '',
    descripcion: p.descripcion ?? '',
    notas: p.notas ?? '',
    gama_id: p.gama_id ?? '',
    categoria_id: p.categoria_id ?? '',
    subcategoria_id: p.subcategoria_id ?? '',
    detalle_id: p.detalle_id ?? '',
    moq: p.moq != null ? String(p.moq) : '',
    lead_time_dias: p.lead_time_dias != null ? String(p.lead_time_dias) : '',
    envio_tipo: p.envio_tipo,
    packaging_included: p.packaging_included,
    foc_pct: p.foc_pct != null ? String(p.foc_pct) : '',
    units_40hq: p.units_40hq != null ? String(p.units_40hq) : '',
    unit_box_largo_cm: p.unit_box_largo_cm != null ? String(p.unit_box_largo_cm) : '',
    unit_box_ancho_cm: p.unit_box_ancho_cm != null ? String(p.unit_box_ancho_cm) : '',
    unit_box_alto_cm: p.unit_box_alto_cm != null ? String(p.unit_box_alto_cm) : '',
    unit_box_peso_kg: p.unit_box_peso_kg != null && p.unit_box_peso_kg !== '' ? String(p.unit_box_peso_kg) : '',
    ctn_largo_cm: p.ctn_largo_cm != null ? String(p.ctn_largo_cm) : '',
    ctn_ancho_cm: p.ctn_ancho_cm != null ? String(p.ctn_ancho_cm) : '',
    ctn_alto_cm: p.ctn_alto_cm != null ? String(p.ctn_alto_cm) : '',
    ctn_peso_kg: p.ctn_peso_kg != null && p.ctn_peso_kg !== '' ? String(p.ctn_peso_kg) : '',
    cbm_ctn: p.cbm_ctn != null && p.cbm_ctn !== '' ? String(p.cbm_ctn) : '',
    carton_box_pcs: p.carton_box_pcs != null ? String(p.carton_box_pcs) : '',
    origen: p.origen ?? '',
    sample: p.sample ?? '',
    precio_target_usd: p.precio_target_usd != null ? String(p.precio_target_usd) : '',
    activo: p.activo,
  };
}

function patchFromDraft(d: Draft): Record<string, unknown> {
  const num = (s: string): number | null => (s === '' ? null : Number(s));
  return {
    ref_proveedor: d.ref_proveedor.trim(),
    nombre: d.nombre.trim(),
    main_specs: d.main_specs.trim() || null,
    descripcion: d.descripcion.trim() || null,
    notas: d.notas.trim() || null,
    gama_id: d.gama_id || null,
    categoria_id: d.categoria_id || null,
    subcategoria_id: d.subcategoria_id || null,
    detalle_id: d.detalle_id || null,
    moq: num(d.moq),
    lead_time_dias: num(d.lead_time_dias),
    envio_tipo: d.envio_tipo,
    packaging_included: d.packaging_included,
    foc_pct: num(d.foc_pct),
    units_40hq: num(d.units_40hq),
    unit_box_largo_cm: num(d.unit_box_largo_cm),
    unit_box_ancho_cm: num(d.unit_box_ancho_cm),
    unit_box_alto_cm: num(d.unit_box_alto_cm),
    unit_box_peso_kg: num(d.unit_box_peso_kg),
    ctn_largo_cm: num(d.ctn_largo_cm),
    ctn_ancho_cm: num(d.ctn_ancho_cm),
    ctn_alto_cm: num(d.ctn_alto_cm),
    ctn_peso_kg: num(d.ctn_peso_kg),
    cbm_ctn: num(d.cbm_ctn),
    carton_box_pcs: num(d.carton_box_pcs),
    origen: d.origen.trim() || null,
    sample: d.sample || null,
    precio_target_usd: num(d.precio_target_usd),
    activo: d.activo,
  };
}

// ─── Componente principal ────────────────────────────────────────────

export function ProductoProveedorDetalle() {
  const { id = '' } = useParams<{ id: string }>();
  return <ProductoProveedorDetalleView id={id} />;
}

/**
 * Cuerpo de la ficha de producto de proveedor. Se usa como página propia (ruta
 * /proveedores/productos/:id) y embebido en la pestaña "Datos proveedor" de la
 * ficha de producto GAME/TICNOVA (mismo registro → cambios recíprocos). En modo
 * `embedded` se ocultan los botones de página (Volver, Nueva cotización,
 * Eliminar) porque la ficha contenedora ya aporta su propia navegación.
 */
export function ProductoProveedorDetalleView({ id, embedded = false }: { id: string; embedded?: boolean }) {
  const navigate = useNavigate();
  const volver = useVolver('/proveedores/productos');
  const qc = useQueryClient();

  const producto = useQuery({
    queryKey: ['producto-proveedor', id, 'full'],
    queryFn: () => api.get<ProductoProveedorFull>(`/productos-proveedor/${id}/full`),
    enabled: !!id,
  });

  const paramContenedor = useQuery({
    queryKey: ['param', 'precio_contenedor_eur'],
    queryFn: () => api.get<{ valor: string }>('/parametros/precio_contenedor_eur'),
  });
  const gamas = useQuery({ queryKey: ['list', '/admin/gamas'], queryFn: () => api.get<{ data: Array<{ id: string; nombre: string }> }>('/admin/gamas') });
  const categorias = useQuery({ queryKey: ['list', '/admin/categorias'], queryFn: () => api.get<{ data: Array<{ id: string; nombre: string }> }>('/admin/categorias') });
  const subcats = useQuery({ queryKey: ['list', '/admin/subcategorias'], queryFn: () => api.get<{ data: Array<{ id: string; nombre: string; categoria_id: string }> }>('/admin/subcategorias') });
  const detalles = useQuery({ queryKey: ['list', '/admin/detalles'], queryFn: () => api.get<{ data: Array<{ id: string; nombre: string; subcategoria_id: string; activo: boolean }> }>('/admin/detalles') });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put(`/productos-proveedor/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto-proveedor', id, 'full'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
    },
  });

  const eliminar = useMutation({
    mutationFn: () => api.delete(`/productos-proveedor/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      navigate('/proveedores/productos');
    },
  });

  // Duplicado: el servidor crea la copia (misma referencia + `*`, campos,
  // histórico de cotizaciones y archivos) y devolvemos al usuario a la ficha
  // nueva, que es donde va a seguir trabajando.
  type DuplicadoResp = {
    id: string;
    ref_proveedor: string;
    precios_copiados: number;
    archivos_copiados: number;
    archivos_fallidos: number;
  };
  const duplicar = useMutation({
    mutationFn: () => api.post<DuplicadoResp>(`/productos-proveedor/${id}/duplicar`, {}),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      if (data.archivos_fallidos > 0) {
        toast.warning('Copia creada con avisos', `Copia creada como "${data.ref_proveedor}", pero ${data.archivos_fallidos} archivo(s) no se pudieron copiar. Revísalos en la ficha nueva.`);
      }
      navigate(`/proveedores/productos/${data.id}`);
    },
    onError: (err: Error) => toast.error('No se pudo duplicar el producto', err.message),
  });

  // ── Nueva cotización desde la ficha ──────────────────────────────
  // Mismo flujo que el botón "Cotizar" del listado: elegimos MARCA
  // (agrupada por cliente) y abrimos la calculadora con ?pp=&marca=.
  const [cotizarOpen, setCotizarOpen] = useState(false);
  const [cotizarMarcaId, setCotizarMarcaId] = useState('');
  const clientesQ = useQuery({
    queryKey: ['list', '/admin/clientes'],
    queryFn: () => api.get<{ data: { id: string; codigo: string; nombre: string }[] }>('/admin/clientes'),
  });
  const marcasQ = useQuery({
    queryKey: ['list', '/admin/marcas'],
    queryFn: () => api.get<{ data: { id: string; cliente_id: string; codigo: string; nombre: string }[] }>('/admin/marcas'),
  });
  type MarcaOpt = { id: string; nombre: string; clienteCodigo: string };
  const marcasPorCliente = useMemo(() => {
    const cliMap = new Map((clientesQ.data?.data ?? []).map((c) => [c.id, c]));
    const groups = new Map<string, { codigo: string; nombre: string; marcas: MarcaOpt[] }>();
    for (const m of marcasQ.data?.data ?? []) {
      const cli = cliMap.get(m.cliente_id);
      const key = cli?.id ?? 'sin-cliente';
      if (!groups.has(key)) {
        groups.set(key, { codigo: cli?.codigo ?? 'Sin cliente', nombre: cli?.nombre ?? 'Sin cliente', marcas: [] });
      }
      groups.get(key)!.marcas.push({ id: m.id, nombre: m.nombre || m.codigo, clienteCodigo: cli?.codigo ?? '' });
    }
    for (const g of groups.values()) g.marcas.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return Array.from(groups.values()).sort((a, b) => a.codigo.localeCompare(b.codigo, 'es'));
  }, [marcasQ.data, clientesQ.data]);
  const marcaLookup = useMemo(() => {
    const out = new Map<string, MarcaOpt>();
    for (const g of marcasPorCliente) for (const m of g.marcas) out.set(m.id, m);
    return out;
  }, [marcasPorCliente]);

  const p = producto.data;

  const subcatsFiltradas = useMemo(() => {
    const all = subcats.data?.data ?? [];
    const catId = editing ? draft?.categoria_id : p?.categoria_id;
    if (!catId) return all;
    return all.filter((s) => s.categoria_id === catId);
  }, [editing, draft?.categoria_id, p?.categoria_id, subcats.data]);
  const detallesForSub = useMemo(() => {
    const all = (detalles.data?.data ?? []).filter((d) => d.activo);
    const subId = editing ? draft?.subcategoria_id : p?.subcategoria_id;
    if (!subId) return [];
    return all.filter((d) => d.subcategoria_id === subId);
  }, [editing, draft?.subcategoria_id, p?.subcategoria_id, detalles.data]);

  if (producto.isLoading) return <Spinner />;
  if (producto.isError || !producto.data || !p) {
    return (
      <div>
        <PageHeader title="Producto de proveedor no encontrado" />
        <Button variant="secondary" onClick={volver}>← Volver</Button>
      </div>
    );
  }

  const m3Source = editing && draft ? {
    largo: Number(draft.unit_box_largo_cm) || 0,
    ancho: Number(draft.unit_box_ancho_cm) || 0,
    alto: Number(draft.unit_box_alto_cm) || 0,
  } : {
    largo: Number(p.unit_box_largo_cm) || 0,
    ancho: Number(p.unit_box_ancho_cm) || 0,
    alto: Number(p.unit_box_alto_cm) || 0,
  };
  const m3 = (m3Source.largo > 0 && m3Source.ancho > 0 && m3Source.alto > 0)
    ? (m3Source.largo * m3Source.ancho * m3Source.alto) / 1_000_000
    : null;
  const uds40hqEstimadas = m3 != null && m3 > 0 ? Math.floor(67 / m3) : null;

  // ── Dimensiones del CTN (master carton) y derivados ──────────────────
  const ctnSource = editing && draft ? {
    largo: Number(draft.ctn_largo_cm) || 0,
    ancho: Number(draft.ctn_ancho_cm) || 0,
    alto: Number(draft.ctn_alto_cm) || 0,
  } : {
    largo: Number(p.ctn_largo_cm) || 0,
    ancho: Number(p.ctn_ancho_cm) || 0,
    alto: Number(p.ctn_alto_cm) || 0,
  };
  // CBM/CTN derivado de las dimensiones del master carton.
  const cbmCtnDerivado = (ctnSource.largo > 0 && ctnSource.ancho > 0 && ctnSource.alto > 0)
    ? (ctnSource.largo * ctnSource.ancho * ctnSource.alto) / 1_000_000
    : null;
  // CBM/CTN manual (introducido en la ficha). Si está informado, gana sobre
  // el derivado: permite calcular el coste de envío sin medir la caja.
  const cbmCtnManual = editing && draft
    ? (Number(draft.cbm_ctn) || 0)
    : (Number(p.cbm_ctn) || 0);
  const cbmCtn = cbmCtnManual > 0 ? cbmCtnManual : cbmCtnDerivado;

  const units40hq = editing && draft
    ? (Number(draft.units_40hq) || 0)
    : (Number(p.units_40hq) || 0);
  const cartonBoxPcs = editing && draft
    ? (Number(draft.carton_box_pcs) || 0)
    : (Number(p.carton_box_pcs) || 0);

  // Coste envío unitario: el contenedor 40HQ tiene 67 CBM. Buscamos la
  // mejor estimación de "unidades por 40HQ" en este orden:
  //  (1) si el producto lo trae explícito (units_40hq), úsalo
  //  (2) si tenemos CBM/CTN y unidades por CTN, calculamos
  //      cajas_por_40HQ × unidades_por_caja
  //  (3) si tenemos volumen de caja unitaria, 67 / m3_unitario
  let units40hqEffective: number | null = null;
  let units40hqFuente: 'directo' | 'ctn' | 'caja' | null = null;
  if (units40hq > 0) {
    units40hqEffective = units40hq;
    units40hqFuente = 'directo';
  } else if (cbmCtn != null && cbmCtn > 0 && cartonBoxPcs > 0) {
    units40hqEffective = Math.floor(67 / cbmCtn) * cartonBoxPcs;
    units40hqFuente = 'ctn';
  } else if (uds40hqEstimadas != null) {
    units40hqEffective = uds40hqEstimadas;
    units40hqFuente = 'caja';
  }
  const precioContenedorEur = Number(paramContenedor.data?.valor) || 0;

  // Coste de envío unitario con la MISMA cascada que la cotización: producto
  // (uds/40HQ propias o derivadas) > detalle > subcategoría > categoría,
  // aplicando el coste de contenedor vigente. Así SIEMPRE sale un coste de
  // envío aunque el producto no tenga datos logísticos propios: se hereda.
  const nNum = (v: number | string | null | undefined) => (Number(v) || 0);
  const u40Cascada = (units40hqEffective && units40hqEffective > 0)
    ? units40hqEffective
    : nNum(p.detalle_units_40hq) || nNum(p.subcat_units_40hq) || 0;
  type EnvioFuente = 'directo' | 'ctn' | 'caja' | 'detalle' | 'subcategoría' | 'categoría';
  let costeEnvioUnitario: number | null = null;
  let costeEnvioFuente: EnvioFuente | null = null;
  if (u40Cascada > 0 && precioContenedorEur > 0) {
    costeEnvioUnitario = precioContenedorEur / u40Cascada;
    costeEnvioFuente = units40hqFuente
      ?? (nNum(p.detalle_units_40hq) > 0 ? 'detalle' : 'subcategoría');
  } else {
    const base = nNum(p.detalle_transporte_base) || nNum(p.subcat_transporte_base) || nNum(p.categoria_transporte_base);
    if (base > 0) {
      costeEnvioUnitario = base;
      costeEnvioFuente = nNum(p.detalle_transporte_base) > 0 ? 'detalle'
        : nNum(p.subcat_transporte_base) > 0 ? 'subcategoría' : 'categoría';
    }
  }

  // Unidades por palet (americano 120×100×220, europeo 120×80×180).
  // Asumimos ~15 cm de altura para la base del palet, restamos del alto
  // total para calcular cuántos niveles de cajas caben. Para la base
  // probamos las dos orientaciones de la caja y nos quedamos con la
  // configuración que más cajas mete.
  function cajasPorPalet(baseX: number, baseY: number, altoUtil: number): number | null {
    if (ctnSource.largo <= 0 || ctnSource.ancho <= 0 || ctnSource.alto <= 0) return null;
    const orientacionA = Math.floor(baseX / ctnSource.largo) * Math.floor(baseY / ctnSource.ancho);
    const orientacionB = Math.floor(baseX / ctnSource.ancho) * Math.floor(baseY / ctnSource.largo);
    const cajasPorNivel = Math.max(orientacionA, orientacionB);
    const niveles = Math.floor(altoUtil / ctnSource.alto);
    if (cajasPorNivel <= 0 || niveles <= 0) return 0;
    return cajasPorNivel * niveles;
  }
  const cajasPaletUsa = cajasPorPalet(120, 100, 220 - 15);
  const cajasPaletEu = cajasPorPalet(120, 80, 180 - 15);
  const udsPaletUsa = cajasPaletUsa != null && cartonBoxPcs > 0 ? cajasPaletUsa * cartonBoxPcs : null;
  const udsPaletEu = cajasPaletEu != null && cartonBoxPcs > 0 ? cajasPaletEu * cartonBoxPcs : null;

  const startEdit = () => {
    setDraft(draftFromProducto(p));
    setEditing(true);
  };
  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };
  const saveEdit = async () => {
    if (!draft) return;
    await update.mutateAsync(patchFromDraft(draft));
    setEditing(false);
    setDraft(null);
  };

  const readField = (v: string | number | null | undefined, suffix?: string) => {
    if (v == null || v === '') return <span className="text-slate-400">—</span>;
    return <span className="text-slate-900">{v}{suffix ? ` ${suffix}` : ''}</span>;
  };

  return (
    <div>
      <PageHeader
        title={p.nombre}
        subtitle={
          <>
            <span>{p.proveedor_nombre ?? '—'} · Ref {p.ref_proveedor}{p.gama_nombre ? ` · ${p.gama_nombre}` : ''}</span>
            <span className="ml-3 text-xs text-slate-400">
              Creado {fmt.date(p.created_at)} · Editado {fmt.datetime(p.updated_at)}
            </span>
          </>
        }
        actions={
          <>
            {!embedded && <Button variant="secondary" onClick={volver}>← Volver</Button>}
            {!editing && (
              <>
                {!embedded && <Button variant="create" onClick={() => { setCotizarMarcaId(''); setCotizarOpen(true); }}>+ Nueva cotización</Button>}
                <Button variant="edit" onClick={startEdit}>✏️ Editar</Button>
                {!embedded && (
                  <Button
                    variant="secondary"
                    onClick={async () => {
                      const ok = await confirmar({
                        title: `¿Duplicar "${p.nombre}"?`,
                        message: `Se creará un producto nuevo con la referencia "${p.ref_proveedor}*" `
                        + 'copiando todos los campos, el histórico de cotizaciones, las imágenes y los archivos.\n\n'
                        + 'Al terminar se abrirá la ficha de la copia.',
                        confirmLabel: 'Duplicar',
                      });
                      if (!ok) return;
                      duplicar.mutate();
                    }}
                    disabled={duplicar.isPending}
                  >
                    {duplicar.isPending ? 'Duplicando…' : '⧉ Duplicar'}
                  </Button>
                )}
                {!embedded && (
                  <Button
                    variant="danger"
                    onClick={async () => {
                      if (!(await confirmar({ title: `¿Eliminar "${p.nombre}"?`, message: 'Se podrá recuperar desde base de datos.', confirmLabel: 'Eliminar', danger: true }))) return;
                      eliminar.mutate();
                    }}
                    disabled={eliminar.isPending}
                  >
                    {eliminar.isPending ? 'Eliminando…' : '🗑 Eliminar'}
                  </Button>
                )}
              </>
            )}
            {editing && (
              <>
                <Button variant="danger" onClick={cancelEdit} disabled={update.isPending}>Cancelar</Button>
                <Button variant="save" onClick={saveEdit} disabled={update.isPending}>
                  {update.isPending ? 'Guardando…' : 'Guardar'}
                </Button>
              </>
            )}
          </>
        }
      />

      {/* Embebida (pestaña Proveedor de la ficha de producto) hay mucho menos
          ancho que en la página propia: con las 12 columnas, Precios y
          Logística quedaban de ~200 px y el texto se montaba en la columna
          vecina. Ahí van siempre a dos columnas; en la página propia, a 12 solo
          con pantalla muy ancha (1800 px; a 1600 seguía sin caber). */}
      <div className={`mb-6 grid gap-4 md:grid-cols-2 ${embedded ? '' : 'min-[1800px]:grid-cols-12'}`}>
        <Card title="Identidad" className={embedded ? '' : 'min-[1800px]:col-span-4'}>
          <dl className="space-y-1.5 text-sm">
            <Row k="Proveedor">{p.proveedor_nombre ?? '—'}</Row>
            <Row k="Model No.">
              {editing ? (
                <Input value={draft!.ref_proveedor} onChange={(e) => setDraft((d) => (d ? { ...d, ref_proveedor: e.target.value } : d))} className="w-full" />
              ) : (
                <span className="font-mono">{p.ref_proveedor}</span>
              )}
            </Row>
            {editing && (
              <Row k="Nombre">
                <Input value={draft!.nombre} onChange={(e) => setDraft((d) => (d ? { ...d, nombre: e.target.value } : d))} className="w-full" />
              </Row>
            )}
            <Row k="Gama">
              {editing ? (
                <Select
                  options={[{ value: '', label: '—' }, ...((gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre })))]}
                  value={draft!.gama_id}
                  emptyLabel={null}
                  onChange={(e) => setDraft((d) => (d ? { ...d, gama_id: e.target.value } : d))}
                  className="w-full"
                />
              ) : (
                readField(p.gama_nombre)
              )}
            </Row>
            <Row k="Categoría">
              {editing ? (
                <Select
                  options={[{ value: '', label: '—' }, ...((categorias.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre })))]}
                  value={draft!.categoria_id}
                  emptyLabel={null}
                  onChange={(e) => setDraft((d) => (d ? { ...d, categoria_id: e.target.value, subcategoria_id: '' } : d))}
                  className="w-full"
                />
              ) : (
                readField(p.categoria_nombre)
              )}
            </Row>
            <Row k="Subcategoría">
              {editing ? (
                <Select
                  options={[{ value: '', label: '—' }, ...subcatsFiltradas.map((s) => ({ value: s.id, label: s.nombre }))]}
                  value={draft!.subcategoria_id}
                  emptyLabel={null}
                  onChange={(e) => setDraft((d) => (d ? { ...d, subcategoria_id: e.target.value, detalle_id: '' } : d))}
                  disabled={!draft!.categoria_id}
                  className="w-full"
                />
              ) : (
                readField(p.subcategoria_nombre)
              )}
            </Row>
            <Row k="Detalle">
              {editing ? (
                <Select
                  options={[{ value: '', label: '—' }, ...detallesForSub.map((d) => ({ value: d.id, label: d.nombre }))]}
                  value={draft!.detalle_id}
                  emptyLabel={null}
                  onChange={(e) => setDraft((d) => (d ? { ...d, detalle_id: e.target.value } : d))}
                  disabled={!draft!.subcategoria_id}
                  className="w-full"
                />
              ) : (
                readField(p.detalle_nombre)
              )}
            </Row>
            <Row k="Main specs">
              {editing ? (
                <Input value={draft!.main_specs} onChange={(e) => setDraft((d) => (d ? { ...d, main_specs: e.target.value } : d))} className="w-full" />
              ) : (
                <span className="text-right">{p.main_specs ?? '—'}</span>
              )}
            </Row>
            <Row k="MOQ">
              {editing ? (
                <Input type="number" value={draft!.moq} onChange={(e) => setDraft((d) => (d ? { ...d, moq: e.target.value } : d))} className="w-full" />
              ) : (
                readField(p.moq != null ? fmt.int(p.moq) : null)
              )}
            </Row>
            <Row k="Lead time (días)">
              {editing ? (
                <Input type="number" value={draft!.lead_time_dias} onChange={(e) => setDraft((d) => (d ? { ...d, lead_time_dias: e.target.value } : d))} className="w-full" />
              ) : (
                readField(p.lead_time_dias)
              )}
            </Row>
            <Row k="Activo">
              {editing ? (
                <Select
                  options={[{ value: 'true', label: 'Sí' }, { value: 'false', label: 'No' }]}
                  value={String(draft!.activo)}
                  emptyLabel={null}
                  onChange={(e) => setDraft((d) => (d ? { ...d, activo: e.target.value === 'true' } : d))}
                  className="w-full"
                />
              ) : (
                <span className={p.activo ? 'text-emerald-700' : 'text-rose-600'}>{p.activo ? 'Sí' : 'No'}</span>
              )}
            </Row>
            <Row k="Origen">
              {editing ? (
                <Input
                  value={draft!.origen}
                  onChange={(e) => setDraft((d) => (d ? { ...d, origen: e.target.value } : d))}
                  placeholder="IMPORTACION, MANUAL, SCRAPING…"
                  className="w-full"
                />
              ) : (
                p.origen ? <span className="text-slate-900">{p.origen}</span> : <span className="text-slate-400">—</span>
              )}
            </Row>
            <Row k="Fecha creación">
              <span className="text-slate-700">{fmt.date(p.created_at)}</span>
            </Row>
          </dl>
        </Card>

        <div className={embedded ? '' : 'min-[1800px]:col-span-3'}>
          <ImagenPrincipalCard productoProveedorId={p.id} />
        </div>

        <div className={`flex flex-col gap-4 ${embedded ? '' : 'min-[1800px]:col-span-2'}`}>
        <Card title="Términos">
          <dl className="space-y-1.5 text-sm">
            {/* Envío (FOB/EXW), Packaging incluido y F.O.C. se gestionan ahora
                en la COTIZACIÓN (la calculadora), no en la ficha del producto
                de proveedor. Decisión CEO 2026-06-03. */}
            <Row k="Sample">
              {editing ? (
                <Select
                  options={[
                    { value: '', label: '— sin definir —' },
                    { value: 'SOLICITADO', label: 'Solicitado' },
                    { value: 'SI', label: 'Sí' },
                    { value: 'NO', label: 'No' },
                  ]}
                  value={draft!.sample}
                  emptyLabel={null}
                  onChange={(e) => setDraft((d) => (d ? { ...d, sample: e.target.value as Draft['sample'] } : d))}
                  className="w-full"
                />
              ) : (
                p.sample
                  ? <span className={
                      p.sample === 'SI' ? 'font-semibold text-emerald-700'
                        : p.sample === 'SOLICITADO' ? 'font-semibold text-amber-600'
                        : 'text-slate-500'
                    }>{p.sample === 'SI' ? 'Sí' : p.sample === 'SOLICITADO' ? 'Solicitado' : 'No'}</span>
                  : <span className="text-slate-400">—</span>
              )}
            </Row>
          </dl>
        </Card>

        <Card title="Precios">
          <dl className="space-y-1.5 text-sm">
            <Row k="Última cotización">
              {p.precios.length > 0
                ? <PrecioMasReciente precios={p.precios} />
                : <span className="text-slate-400">—</span>}
            </Row>
            <Row k="Target (USD)">
              {editing ? (
                <Input
                  type="number"
                  step="any"
                  value={draft!.precio_target_usd}
                  onChange={(e) => setDraft((d) => (d ? { ...d, precio_target_usd: e.target.value } : d))}
                  className="w-full"
                  placeholder="USD FOB objetivo"
                />
              ) : (
                p.precio_target_usd != null
                  ? <span className="font-semibold text-emerald-700">{fmt.usd(p.precio_target_usd)}</span>
                  : <span className="text-slate-400">—</span>
              )}
            </Row>
          </dl>
        </Card>
        </div>

        <Card title="Logística" className={embedded ? '' : 'min-[1800px]:col-span-3'}>
          <dl className="space-y-1.5 text-sm">
            <Row k="Units / 40HQ">
              {editing ? (
                <Input type="number" value={draft!.units_40hq} onChange={(e) => setDraft((d) => (d ? { ...d, units_40hq: e.target.value } : d))} className="w-full" />
              ) : (
                readField(p.units_40hq != null ? fmt.int(p.units_40hq) : null)
              )}
            </Row>
            <Row k="Unit box (LxAxA cm)" ancho={editing}>
              {editing ? (
                <div className="flex w-full items-center gap-1.5">
                  <Input type="number" step="any" value={draft!.unit_box_largo_cm} onChange={(e) => setDraft((d) => (d ? { ...d, unit_box_largo_cm: e.target.value } : d))} className={DIM_INPUT} placeholder="Largo" />
                  <span className="shrink-0 text-slate-400">×</span>
                  <Input type="number" step="any" value={draft!.unit_box_ancho_cm} onChange={(e) => setDraft((d) => (d ? { ...d, unit_box_ancho_cm: e.target.value } : d))} className={DIM_INPUT} placeholder="Ancho" />
                  <span className="shrink-0 text-slate-400">×</span>
                  <Input type="number" step="any" value={draft!.unit_box_alto_cm} onChange={(e) => setDraft((d) => (d ? { ...d, unit_box_alto_cm: e.target.value } : d))} className={DIM_INPUT} placeholder="Alto" />
                </div>
              ) : (
                p.unit_box_largo_cm && p.unit_box_ancho_cm && p.unit_box_alto_cm
                  ? `${fmt.number(p.unit_box_largo_cm)} × ${fmt.number(p.unit_box_ancho_cm)} × ${fmt.number(p.unit_box_alto_cm)}`
                  : '—'
              )}
            </Row>
            <Row k="Peso unit box (kg)">
              {editing ? (
                <Input type="number" step="any" value={draft!.unit_box_peso_kg} onChange={(e) => setDraft((d) => (d ? { ...d, unit_box_peso_kg: e.target.value } : d))} className="w-full" placeholder="kg" />
              ) : (
                readField(p.unit_box_peso_kg != null && p.unit_box_peso_kg !== '' ? `${fmt.number(p.unit_box_peso_kg)} kg` : null)
              )}
            </Row>
            <Row k="Volumen caja (m³)">{m3 != null ? m3.toFixed(4) : '—'}</Row>
            <Row k="Estimación 40HQ">{uds40hqEstimadas != null ? `${fmt.int(uds40hqEstimadas)} ud` : '—'}</Row>
            <Row k="Carton box (pcs)">
              {editing ? (
                <Input type="number" value={draft!.carton_box_pcs} onChange={(e) => setDraft((d) => (d ? { ...d, carton_box_pcs: e.target.value } : d))} className="w-full" />
              ) : (
                readField(p.carton_box_pcs != null ? fmt.int(p.carton_box_pcs) : null)
              )}
            </Row>
            <Row k="CBM / Carton box (m³)">
              {editing ? (
                <div className="flex w-full items-center gap-2">
                  <Input
                    type="number"
                    step="any"
                    value={draft!.cbm_ctn}
                    onChange={(e) => setDraft((d) => (d ? { ...d, cbm_ctn: e.target.value } : d))}
                    className="w-28"
                    placeholder={cbmCtnDerivado != null ? cbmCtnDerivado.toFixed(3) : 'CBM/CTN'}
                  />
                  <span className="text-[10px] text-slate-400">
                    {cbmCtnDerivado != null
                      ? `derivado ${cbmCtnDerivado.toFixed(3)} · vacío = usar derivado`
                      : 'vacío = derivar de dimensiones'}
                  </span>
                </div>
              ) : (
                <span>
                  {cbmCtn != null ? cbmCtn.toFixed(3) : '—'}
                  {cbmCtnManual > 0 && <span className="ml-1 text-[10px] text-slate-400">(manual)</span>}
                </span>
              )}
            </Row>
            <Row k="CTN - Carton Size Box (LxAxA cm)" ancho={editing}>
              {editing ? (
                <div className="flex w-full items-center gap-1.5">
                  <Input type="number" step="any" value={draft!.ctn_largo_cm} onChange={(e) => setDraft((d) => (d ? { ...d, ctn_largo_cm: e.target.value } : d))} className={DIM_INPUT} placeholder="Largo" />
                  <span className="shrink-0 text-slate-400">×</span>
                  <Input type="number" step="any" value={draft!.ctn_ancho_cm} onChange={(e) => setDraft((d) => (d ? { ...d, ctn_ancho_cm: e.target.value } : d))} className={DIM_INPUT} placeholder="Ancho" />
                  <span className="shrink-0 text-slate-400">×</span>
                  <Input type="number" step="any" value={draft!.ctn_alto_cm} onChange={(e) => setDraft((d) => (d ? { ...d, ctn_alto_cm: e.target.value } : d))} className={DIM_INPUT} placeholder="Alto" />
                </div>
              ) : (
                p.ctn_largo_cm && p.ctn_ancho_cm && p.ctn_alto_cm
                  ? `${fmt.number(p.ctn_largo_cm)} × ${fmt.number(p.ctn_ancho_cm)} × ${fmt.number(p.ctn_alto_cm)}`
                  : '—'
              )}
            </Row>
            <Row k="Peso carton box (kg)">
              {editing ? (
                <Input type="number" step="any" value={draft!.ctn_peso_kg} onChange={(e) => setDraft((d) => (d ? { ...d, ctn_peso_kg: e.target.value } : d))} className="w-full" placeholder="kg" />
              ) : (
                readField(p.ctn_peso_kg != null && p.ctn_peso_kg !== '' ? `${fmt.number(p.ctn_peso_kg)} kg` : null)
              )}
            </Row>
            <Row k="Coste envío unitario">
              {costeEnvioUnitario != null ? (
                <span className="font-mono text-emerald-700" title={
                  costeEnvioFuente === 'directo' ? 'Calculado con units_40hq directo del producto.'
                  : costeEnvioFuente === 'ctn' ? 'Calculado a partir de CBM/CTN y unidades por CTN.'
                  : costeEnvioFuente === 'caja' ? 'Calculado a partir del volumen de la caja unitaria.'
                  : `Heredado de ${costeEnvioFuente} (el producto no tiene datos logísticos propios). Aplica el coste de contenedor vigente.`
                }>
                  {fmt.eur(Number(costeEnvioUnitario.toFixed(2)))}
                  <span className="ml-1 text-[10px] text-slate-400">
                    ({costeEnvioFuente === 'directo' ? '40HQ directo'
                      : costeEnvioFuente === 'ctn' ? 'vía CTN'
                      : costeEnvioFuente === 'caja' ? 'vía caja unitaria'
                      : `heredado de ${costeEnvioFuente}`})
                  </span>
                </span>
              ) : (
                <span className="text-slate-400">— (sin datos en producto, detalle, subcategoría ni categoría)</span>
              )}
            </Row>
            <Row k="Unidades / Palet Americano">
              <span title="Palet 120×100×220 cm. Asume 15 cm de base de palet.">
                {udsPaletUsa != null ? `${fmt.int(udsPaletUsa)} ud` : '—'}
                {cajasPaletUsa != null && cartonBoxPcs > 0 && (
                  <span className="ml-1 text-[10px] text-slate-400">({cajasPaletUsa} cajas × {cartonBoxPcs})</span>
                )}
              </span>
            </Row>
            <Row k="Unidades / Palet Europeo">
              <span title="Palet 120×80×180 cm. Asume 15 cm de base de palet.">
                {udsPaletEu != null ? `${fmt.int(udsPaletEu)} ud` : '—'}
                {cajasPaletEu != null && cartonBoxPcs > 0 && (
                  <span className="ml-1 text-[10px] text-slate-400">({cajasPaletEu} cajas × {cartonBoxPcs})</span>
                )}
              </span>
            </Row>
          </dl>
        </Card>
      </div>

      {/* Histórico de precios JUSTO debajo de los datos del producto. */}
      <div className="mb-6">
        <PreciosCard productoProveedorId={p.id} precios={p.precios} />
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <Card title="Descripción extendida">
          {editing ? (
            <Textarea
              rows={6}
              value={draft!.descripcion}
              onChange={(e) => setDraft((d) => (d ? { ...d, descripcion: e.target.value } : d))}
            />
          ) : (
            p.descripcion
              ? <p className="whitespace-pre-line text-sm text-slate-700">{p.descripcion}</p>
              : <p className="text-sm italic text-slate-400">Sin descripción.</p>
          )}
        </Card>
        <Card title="Notas">
          {editing ? (
            <Textarea
              rows={6}
              value={draft!.notas}
              onChange={(e) => setDraft((d) => (d ? { ...d, notas: e.target.value } : d))}
              placeholder="Notas internas sobre el producto: incidencias con el proveedor, comentarios del CEO, etc."
            />
          ) : (
            p.notas
              ? <p className="whitespace-pre-line text-sm text-slate-700">{p.notas}</p>
              : <p className="text-sm italic text-slate-400">Sin notas.</p>
          )}
        </Card>
      </div>

      {/* Imágenes adicionales y documentos adjuntos al final. */}
      <div className="mb-6">
        <GaleriaAdicionalCard productoProveedorId={p.id} />
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <FichaCard productoProveedorId={p.id} />
        <UserGuideCard productoProveedorId={p.id} />
        <SpecsCard productoProveedorId={p.id} />
      </div>

      {/* Modal Nueva cotización — selección de MARCA + abre calculadora */}
      <Modal
        open={cotizarOpen}
        onClose={() => setCotizarOpen(false)}
        title="¿Para qué marca cotizar?"
        size="sm"
        footer={
          <>
            <Button variant="danger" onClick={() => setCotizarOpen(false)}>Cancelar</Button>
            <Button
              variant="create"
              disabled={!cotizarMarcaId}
              onClick={() => {
                const opt = marcaLookup.get(cotizarMarcaId);
                const base = opt?.clienteCodigo === 'TICNOVA' ? '/ticnova/calculadora' : '/game/calculadora';
                navigate(`${base}?pp=${p.id}&marca=${cotizarMarcaId}`);
                setCotizarOpen(false);
              }}
            >
              Cotizar
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          Selecciona la marca para aplicar las condiciones comerciales correspondientes.
        </p>
        <label className="mb-1 block text-xs font-medium text-slate-600">¿A qué marca cotizar?</label>
        <select
          value={cotizarMarcaId}
          onChange={(e) => setCotizarMarcaId(e.target.value)}
          className="block w-full cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        >
          <option value="">— elegir marca —</option>
          {marcasPorCliente.map((g) => (
            <optgroup key={g.codigo} label={`Cliente ${g.codigo}`}>
              {g.marcas.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </Modal>
    </div>
  );
}

/**
 * Cajas de las filas de dimensiones (L × A × A). Antes eran `w-20` fijos:
 * descontando el padding y las flechitas del input numérico quedaban unos
 * 50 px útiles y no se veía lo que se tecleaba. Con `flex-1` se reparten
 * todo el hueco de la fila, y `min-w-0` permite que se encojan por debajo
 * de su ancho de contenido en vez de desbordar. `px-2` recupera 8 px más.
 */
const DIM_INPUT = 'min-w-0 flex-1 px-2 text-center';

/**
 * `ancho` levanta el tope del 60% de la columna de valor. Lo usan las filas
 * de dimensiones (L × A × A), donde hay que meter tres cajas en el hueco y
 * con el tope por defecto quedaban tan estrechas que no se veía lo tecleado.
 */
function Row({ k, children, ancho = false }: { k: string; children: React.ReactNode; ancho?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="min-w-0 shrink text-slate-500">{k}</dt>
      <dd className={`min-w-0 flex-1 break-words text-right text-slate-900 ${ancho ? '' : 'max-w-[60%]'}`}>{children}</dd>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tabla de precios histórica (modal independiente para cada cotización)
// ─────────────────────────────────────────────────────────────────────

type OpcionDraft = { descripcion: string; importe_usd: string; incluido: boolean };

type PrecioDraft = {
  fecha: string;
  tramo1_unidades: string; tramo1_precio_usd: string;
  tramo2_unidades: string; tramo2_precio_usd: string;
  tramo3_unidades: string; tramo3_precio_usd: string;
  opciones: OpcionDraft[];
  notas: string;
  color: string;
};

function emptyDraft(): PrecioDraft {
  return {
    fecha: new Date().toISOString().slice(0, 10),
    tramo1_unidades: '', tramo1_precio_usd: '',
    tramo2_unidades: '', tramo2_precio_usd: '',
    tramo3_unidades: '', tramo3_precio_usd: '',
    opciones: [],
    notas: '',
    color: '',
  };
}

function rowToDraft(row: PrecioRow): PrecioDraft {
  return {
    fecha: row.fecha.slice(0, 10),
    tramo1_unidades: row.tramo1_unidades?.toString() ?? '',
    tramo1_precio_usd: row.tramo1_precio_usd?.toString() ?? '',
    tramo2_unidades: row.tramo2_unidades?.toString() ?? '',
    tramo2_precio_usd: row.tramo2_precio_usd?.toString() ?? '',
    tramo3_unidades: row.tramo3_unidades?.toString() ?? '',
    tramo3_precio_usd: row.tramo3_precio_usd?.toString() ?? '',
    opciones: (row.opciones ?? []).map((o) => ({
      descripcion: o.descripcion ?? '',
      importe_usd: o.importe_usd?.toString() ?? '',
      incluido: !!o.incluido,
    })),
    notas: row.notas ?? '',
    color: row.color ?? '',
  };
}

function draftToPayload(d: PrecioDraft): Record<string, unknown> {
  return {
    fecha: d.fecha,
    tramo1_unidades: d.tramo1_unidades === '' ? null : Number(d.tramo1_unidades),
    tramo1_precio_usd: d.tramo1_precio_usd === '' ? null : Number(d.tramo1_precio_usd),
    tramo2_unidades: d.tramo2_unidades === '' ? null : Number(d.tramo2_unidades),
    tramo2_precio_usd: d.tramo2_precio_usd === '' ? null : Number(d.tramo2_precio_usd),
    tramo3_unidades: d.tramo3_unidades === '' ? null : Number(d.tramo3_unidades),
    tramo3_precio_usd: d.tramo3_precio_usd === '' ? null : Number(d.tramo3_precio_usd),
    opciones: d.opciones
      .filter((o) => o.descripcion.trim() !== '' || o.importe_usd !== '')
      .map((o) => ({
        descripcion: o.descripcion.trim(),
        importe_usd: o.importe_usd === '' ? 0 : Number(o.importe_usd),
        incluido: !!o.incluido,
      })),
    notas: d.notas || null,
    color: normalizarColor(d.color),
  };
}

/**
 * Resumen visual de la cotización más reciente de un producto de
 * proveedor. Muestra fecha + los tramos rellenados (uds @ USD).
 */
function PrecioMasReciente({ precios }: { precios: PrecioRow[] }) {
  // Con precios por color, el más reciente de cada color (y el general, si lo
  // hay); sin colores, el más reciente sin más.
  const colores = coloresDePrecios(precios);
  if (colores.length === 0) return <PrecioReciente reciente={precioParaColor(precios, null)} />;
  const hayGeneral = precios.some((x) => !normalizarColor(x.color));
  return (
    <div className="space-y-1.5">
      {colores.map((c) => <PrecioReciente key={c} reciente={precioParaColor(precios, c)} color={c} />)}
      {hayGeneral && <PrecioReciente reciente={precioParaColor(precios, null)} color="General" />}
    </div>
  );
}

function PrecioReciente({ reciente, color }: { reciente: PrecioRow | null; color?: string }) {
  if (!reciente) return <span className="text-slate-400">—</span>;
  const tramos: { uds: number | null; precio: number | null }[] = [
    { uds: reciente.tramo1_unidades, precio: reciente.tramo1_precio_usd },
    { uds: reciente.tramo2_unidades, precio: reciente.tramo2_precio_usd },
    { uds: reciente.tramo3_unidades, precio: reciente.tramo3_precio_usd },
  ].filter((t) => t.uds != null || t.precio != null);
  return (
    <div className="text-right">
      <div className="text-xs text-slate-500">{color && <span className="mr-1 font-semibold text-slate-700">{color}</span>}{fmt.date(reciente.fecha)}</div>
      {tramos.length === 0 ? (
        <span className="text-slate-400">—</span>
      ) : (
        <ul className="space-y-0.5">
          {tramos.map((t, i) => (
            <li key={i} className="font-mono text-xs">
              {t.uds != null ? fmt.int(t.uds) : '?'} ud @ <span className="font-semibold text-slate-900">{t.precio != null ? fmt.usd(t.precio) : '?'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PreciosCard({ productoProveedorId, precios }: { productoProveedorId: string; precios: PrecioRow[] }) {
  const qc = useQueryClient();
  const [modal, setModal] = useState<{ mode: 'create' } | { mode: 'edit'; id: string } | null>(null);
  const [draft, setDraft] = useState<PrecioDraft>(emptyDraft);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['producto-proveedor', productoProveedorId, 'full'] });

  const guardar = useMutation({
    mutationFn: () => {
      const payload = draftToPayload(draft);
      if (modal?.mode === 'edit') {
        return api.patch(`/productos-proveedor/${productoProveedorId}/precios/${modal.id}`, payload);
      }
      return api.post(`/productos-proveedor/${productoProveedorId}/precios`, payload);
    },
    onSuccess: () => {
      invalidate();
      setModal(null);
      setDraft(emptyDraft());
    },
  });

  const borrar = useMutation({
    mutationFn: (precioId: string) => api.delete(`/productos-proveedor/${productoProveedorId}/precios/${precioId}`),
    onSuccess: invalidate,
  });

  const ordenados = useMemo(() => [...precios].sort((a, b) => b.fecha.localeCompare(a.fecha)), [precios]);

  const openCreate = () => { setDraft(emptyDraft()); setModal({ mode: 'create' }); };
  const openEdit = (row: PrecioRow) => { setDraft(rowToDraft(row)); setModal({ mode: 'edit', id: row.id }); };

  return (
    <Card
      title={`Histórico de precios (${precios.length})`}
      description="Cada fila es un precio con fecha, color (opcional: si el proveedor da un precio por color, una fila por color), hasta 3 tramos de unidades/precio y opciones (extras o ítems incluidos). Al cotizar se pregunta el color. El listado del catálogo muestra siempre el más reciente. Click en una fila para editar."
      actions={<Button variant="create" size="sm" onClick={openCreate}>+ Nuevo precio</Button>}
    >
      {precios.length === 0 ? (
        <p className="text-sm italic text-slate-400">Sin cotizaciones registradas.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left">Fecha</th>
                <th className="px-2 py-1.5 text-left">Color</th>
                <th className="px-2 py-1.5 text-right">Tramo 1 (uds @ $)</th>
                <th className="px-2 py-1.5 text-right">Tramo 2</th>
                <th className="px-2 py-1.5 text-right">Tramo 3</th>
                <th className="px-2 py-1.5 text-left">Opciones</th>
                <th className="px-2 py-1.5 text-left">Notas</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ordenados.map((row) => (
                <tr key={row.id} className="cursor-pointer align-top hover:bg-slate-50" onClick={() => openEdit(row)}>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs text-slate-500">{fmt.date(row.fecha)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-xs">{row.color ? <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-700">{row.color}</span> : <span className="text-slate-400">General</span>}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{renderTramo(row.tramo1_unidades, row.tramo1_precio_usd)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{renderTramo(row.tramo2_unidades, row.tramo2_precio_usd)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{renderTramo(row.tramo3_unidades, row.tramo3_precio_usd)}</td>
                  <td className="px-2 py-1.5 text-xs">{renderOpcionesResumen(row.opciones)}</td>
                  <td className="px-2 py-1.5 text-xs">{row.notas ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={async (e) => { e.stopPropagation(); if (await confirmar({ title: '¿Borrar cotización?', confirmLabel: 'Borrar', danger: true })) borrar.mutate(row.id); }}
                      className="cursor-pointer text-xs text-rose-600 hover:text-rose-800"
                    >×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.mode === 'edit' ? 'Editar precio' : 'Nuevo precio'}
        size="lg"
        footer={
          <>
            <Button variant="danger" onClick={() => setModal(null)}>Cancelar</Button>
            <Button variant="save" onClick={() => guardar.mutate()} disabled={guardar.isPending}>Guardar</Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <FormRow label="Fecha"><Input type="date" value={draft.fecha} onChange={(e) => setDraft((d) => ({ ...d, fecha: e.target.value }))} /></FormRow>
            <FormRow label="Color" hint="Vacío = precio general, vale para cualquier color.">
              <Input list={`colores-${productoProveedorId}`} value={draft.color} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value.toUpperCase() }))} placeholder="ej. NEGRO" />
              <datalist id={`colores-${productoProveedorId}`}>
                {[...new Set([...coloresDePrecios(precios), ...COLORES_HABITUALES])].map((c) => <option key={c} value={c} />)}
              </datalist>
            </FormRow>
          </div>
          <div className="space-y-2 rounded-md border border-slate-200 p-3">
            <p className="text-xs font-semibold text-slate-600">Tramos (cuantas más unidades, menor precio).</p>
            {[1, 2, 3].map((n) => {
              const udKey = `tramo${n}_unidades` as 'tramo1_unidades' | 'tramo2_unidades' | 'tramo3_unidades';
              const prKey = `tramo${n}_precio_usd` as 'tramo1_precio_usd' | 'tramo2_precio_usd' | 'tramo3_precio_usd';
              return (
                <div key={n} className="grid grid-cols-2 gap-2">
                  <FormRow label={`Tramo ${n}: unidades`}><Input type="number" value={draft[udKey]} onChange={(e) => setDraft((d) => ({ ...d, [udKey]: e.target.value }))} placeholder="ej. 1000" /></FormRow>
                  <FormRow label={`Precio USD`}><Input type="number" step="any" value={draft[prKey]} onChange={(e) => setDraft((d) => ({ ...d, [prKey]: e.target.value }))} placeholder="ej. 5.20" /></FormRow>
                </div>
              );
            })}
          </div>
          <OpcionesEditor opciones={draft.opciones} onChange={(next) => setDraft((d) => ({ ...d, opciones: next }))} />
          <FormRow label="Notas"><Textarea rows={2} value={draft.notas} onChange={(e) => setDraft((d) => ({ ...d, notas: e.target.value }))} /></FormRow>
        </div>
      </Modal>
    </Card>
  );
}

function OpcionesEditor({ opciones, onChange }: { opciones: OpcionDraft[]; onChange: (next: OpcionDraft[]) => void }) {
  const add = () => onChange([...opciones, { descripcion: '', importe_usd: '', incluido: false }]);
  const remove = (idx: number) => onChange(opciones.filter((_, i) => i !== idx));
  const update = (idx: number, patch: Partial<OpcionDraft>) => onChange(opciones.map((o, i) => (i === idx ? { ...o, ...patch } : o)));

  return (
    <div className="space-y-2 rounded-md border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-600">Opciones / extras</p>
        <button type="button" onClick={add} className="cursor-pointer text-xs text-sky-600 hover:underline">+ Añadir opción</button>
      </div>
      <p className="text-[11px] text-slate-500">
        Items que el proveedor cobra aparte ("extra") o que aclara incluidos en el precio base ("incluido"). Ej.: cable HDMI, manual impreso, certificado CE.
      </p>
      {opciones.length === 0 ? (
        <p className="py-2 text-xs italic text-slate-400">Sin opciones.</p>
      ) : (
        <div className="space-y-1.5">
          {opciones.map((o, idx) => (
            <div key={idx} className="grid grid-cols-12 items-center gap-2">
              <Input
                className="col-span-6"
                placeholder="Descripción (ej. cable HDMI 1.8m)"
                value={o.descripcion}
                onChange={(e) => update(idx, { descripcion: e.target.value })}
              />
              <Input
                className="col-span-3"
                type="number"
                step="any"
                placeholder="USD"
                value={o.importe_usd}
                onChange={(e) => update(idx, { importe_usd: e.target.value })}
              />
              <label className="col-span-2 inline-flex cursor-pointer items-center gap-1.5 text-xs">
                <input type="checkbox" checked={o.incluido} onChange={(e) => update(idx, { incluido: e.target.checked })} className="h-3.5 w-3.5" />
                Incluido
              </label>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="col-span-1 cursor-pointer text-right text-xs text-rose-600 hover:text-rose-800"
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderOpcionesResumen(opciones: PrecioRow['opciones']): React.ReactNode {
  if (!opciones || opciones.length === 0) return <span className="text-slate-300">—</span>;
  return (
    <ul className="space-y-0.5">
      {opciones.map((o, i) => (
        <li key={i} className={o.incluido ? 'text-emerald-700' : 'text-slate-700'}>
          <span className="font-mono text-[11px]">{o.incluido ? '✓' : '+'}</span>{' '}
          {o.descripcion}
          {o.importe_usd ? <span className="ml-1 font-mono text-[11px] text-slate-500">{fmt.usd(o.importe_usd)}</span> : null}
        </li>
      ))}
    </ul>
  );
}

function renderTramo(uds: number | null, precio: number | null): React.ReactNode {
  if (uds == null && precio == null) return <span className="text-slate-300">—</span>;
  return (
    <span>
      {uds != null ? fmt.int(uds) : '?'} @ {precio != null ? fmt.usd(precio) : '?'}
    </span>
  );
}
